<script setup lang="ts">
import { onMounted, ref } from "vue";
import Pagination from "@/commons/components/Pagination.vue";
import { useInstitutionStore } from "@/domains/home/services/nir/associate-registers/stores";
import { useRegistryStore } from "@/domains/home/services/registry/stores";
import { useFormsStore } from "@/domains/home/services/nir/configurations/stores/forms.ts";
import router from "@/router.ts";

const loading = ref(false)
const viewModalOpen = ref(false)

const page = ref(1)
const limit = ref(16)
const selectedErn = ref('')
const nameFilter = ref('')
const ernFilter = ref('')
const entityTypeFilter = ref('')
const subEntityTypeFilter = ref('')
const institutionFilter = ref('')
const statusFilter = ref('')
const totalItems = ref(5)

const institutionStore = useInstitutionStore()
const configStore = useFormsStore()
const store = useRegistryStore()

onMounted(() => {
  institutionStore.fetchInstitutions(1, 1000)
  configStore.fetchPrimaryEntityTypes()
  fetch()
})

function fetch() {
  loading.value = true
  store.fetchEntities(
      ernFilter.value, nameFilter.value, entityTypeFilter.value,
      subEntityTypeFilter.value, institutionFilter.value,
      statusFilter.value, page.value, limit.value).then(() => {
    loading.value = false
  })
}

function view(ern: string) {
  selectedErn.value = ern
  router.push({name: 'nonindividual-viewer', params:{id:ern}})
}

function getStatusColor(status: string) {
  return status === 'REGISTERED' || status === 'COMPLETED' || status === 'COMPLIANT'
    ? 'text-green-700 font-bold bg-green-100'
    : 'text-red-700 bg-red-100'
}

function update(pg: number) {
  page.value = pg
  fetch()
}

function fetchSubTypes() {
  configStore.fetchSubEntityTypes(entityTypeFilter.value)
  fetch()
}
</script>

<template>
  <div class="flex-1 flex flex-col overflow-hidden bg-white p-3">

    <div class="grid grid-cols-6 gap-2 py-2">
      <input
          v-model="ernFilter"
          @change="fetch"
          type="text"
          placeholder="Registration Number"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"/>
      <input
          v-model="nameFilter"
          @change="fetch"
          type="text"
          placeholder="Search name..."
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"/>
      <select
          v-model="entityTypeFilter"
          @change="fetchSubTypes"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option value="">Select Entity Type</option>
        <option v-for="(type, idx) in configStore.primaryEntityTypes" :key="idx" :value="type.id">
          {{ type.name }}
        </option>
      </select>

      <select
          v-model="subEntityTypeFilter"
          @change="fetch"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option value="">Select Sub Entity Type</option>
        <option v-for="(type, idx) in configStore.subEntityTypes" :key="idx" :value="type.id">
          {{ type.name }}
        </option>
      </select>

      <select
          v-model="institutionFilter"
          @change="fetch"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option value="">Select Associate Register</option>
        <option v-for="(institution, idx) in institutionStore.institutions" :key="idx" :value="institution.id">
          {{ institution.name }}
        </option>
      </select>
      <select
          v-model="statusFilter"
          @change="fetch"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option value="">Select Status</option>
        <option value="REGISTERED">Registered</option>
        <option value="DEREGISTERED">De Registered</option>
      </select>

    </div>

    <div class="bg-white shadow overflow-hidden flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="min-w-full text-xs">
          <thead class="bg-gray-50 border-b border-gray-100 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 sticky left-0 z-20 bg-gray-50 w-14">#</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 sticky left-14 z-20 bg-gray-50">ERN</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">Type</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">Sub Type</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">Associate Register</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">Record Status</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">Compliance Status</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="store.entities && store.entities.length > 0">
              <tr
                v-for="(item, idx) in store.entities"
                :key="idx"
                class="border-b border-gray-100 hover:bg-gray-50 transition group">
                <td class="py-3 px-4 font-bold sticky left-0 z-10 bg-white group-hover:bg-gray-50 w-14">
                  <span>{{ idx + 1 }}</span>
                </td>
                <td class="py-3 px-4 font-bold sticky left-14 z-10 bg-white group-hover:bg-gray-50">
                  <span>{{ item.ern }}</span>
                </td>
                <td class="py-3 px-4">
                  {{ item.name.toUpperCase() }}
                </td>
                <td class="py-3 px-4">
                  <span>{{ item.type }}</span>
                </td>
                <td class="py-3 px-4">
                  <span v-if="item.subType">{{ item.subType }}</span>
                  <span v-else>--</span>
                </td>
                <td class="py-3 px-4">
                  <div class="flex gap-2 text-primary">
                    <i class="fas fa-bank my-auto"></i> <span class="my-auto">{{item.registrationInstitution}}</span>
                  </div>
                </td>
                <td class="py-3 px-4">
                  <span class="text-xs font-medium px-3 py-1 rounded-full inline-block text-gray-700 p-1" :class="getStatusColor(item.status)">
                    {{ item.status.replace(/_/g, ' ') }}
                  </span>
                </td>
                <td class="py-3 px-4">
                  <span class="text-xs font-medium px-3 py-1 rounded-full inline-block text-gray-700 p-1" :class="getStatusColor(item.recordStatus)">
                    {{ item.recordStatus.replace(/_/g, '') }}
                  </span>
                </td>
                <td class="py-3 px-4">
                  <span class="text-xs font-medium px-3 py-1 rounded-full inline-block text-gray-700 p-1" :class="getStatusColor(item.complianceStatus)">
                    {{ item.complianceStatus.replace(/_/g, ' ') }}
                  </span>
                </td>
                <td class="py-3 px-4">
                  <button
                      @click="view(item.ern)"
                      class="btn-sm-primary"
                      title="View Details">
                    <i class="fa-solid fa-eye"></i>
                  </button>
                </td>
              </tr>
            </template>
            <template v-if="!store.entities || store.entities && store.entities.length == 0">
              <tr>
                <td colspan="7" class="py-16 text-center">
                  <div class="flex flex-col items-center justify-center text-gray-400">
                    <i class="fa fa-folder-open text-6xl mb-4"></i>
                    <p class="text-lg font-medium text-gray-500">No Entities Found</p>
                    <p class="text-sm text-gray-400 mt-2">There are no registered entities in the registry yet.</p>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <Pagination
        :pagination="store.pagination"
        :page="page"
        :limit="limit"
        label="entities"
        @update="update"
      />
    </div>
  </div>
</template>

<style scoped></style>
